/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/PublicationData/components/PublicationDataFieldRenderer.tsx.
 */

import React, { useEffect, useRef, useState } from "react";
import axiosClient from "../../../../../../api/axiosClient";
import { API_URLS } from "../../../../../../constants/apiUrls";
import { FormField, DropdownOption } from "../../../../../../interfaces/formInterfaces";
import TextInput from "../../../../../../components/common/TextInput";
import Dropdown from "../../../../../../components/common/Dropdown";
import { PUBLICATION_FILE_MAX_SIZE_LABEL } from "../../../../../../constants/publicationFileUpload";
import {
  normalizeEpdProgrammeStoredValue,
  normalizeReferenceServiceLifeYears,
  normalizeSystemBoundaryStoredValue,
  resolveEpdProgrammeDisplayLabel,
  resolveSystemBoundaryDisplayLabel,
} from "../../../../../../constants/publicationEpdFieldOptions";
import {
  normalizeLcaDatabaseStoredValue,
  resolveLcaDatabaseDisplayLabel,
} from "../../../../../../constants/publicationLcaDatabaseOptions";
import FormFieldLabel from "../../../../../../components/common/FormFieldLabel";
import { getContributorTextInputHtmlProps } from "../../../../../../utils/contributorFormFieldInputProps";
import { formatUnitOptionLabel } from "../../../../../../utils/formatUnitSymbol";

type CmsUnitRow = {
  id?: number;
  symbol?: string;
  unitSymbol?: string;
  name?: string;
  displayName?: string;
};

const PublicationUnitField: React.FC<{
  field: FormField;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  error?: string;
  disabled?: boolean;
}> = ({ field, value, onChange, error, disabled = false }) => {
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (field.unitPropertyId) {
        setLoading(true);
        try {
          const res = await axiosClient.get(API_URLS.UNITS.BY_PROPERTY(field.unitPropertyId));
          if (res.data?.success && Array.isArray(res.data.data)) {
            const rows = res.data.data as CmsUnitRow[];
            setOptions(
              rows.map((u) => ({
                value: String(u.id ?? ""),
                label: formatUnitOptionLabel(u),
              }))
            );
          } else {
            setOptions([]);
          }
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
        return;
      }

      const cms: DropdownOption[] = field.dropdown?.options || field.dropdownValues || [];
      setOptions(
        cms.map((opt) => ({
          label: opt.label,
          value: String(opt.value ?? ""),
        }))
      );
    };

    void load();
  }, [field.id, field.unitPropertyId, field.dropdown, field.dropdownValues]);

  const selected = String(value ?? "").trim();
  const idInList = options.some((o) => o.value === selected);
  const merged =
    selected && !idInList
      ? [{ value: selected, label: `Saved unit (${selected})` }, ...options]
      : options;

  const isFieldDisabled =
    field.type === "READONLY" ||
    field.isEditable === false ||
    disabled ||
    loading;

  return (
    <Dropdown
      key={field.id}
      label={field.label}
      name={field.name}
      options={merged}
      value={selected}
      onChange={onChange}
      required={field.isMandatory}
      disabled={isFieldDisabled}
      infoText={field.infoText}
      error={error}
      placeholder="Select unit…"
    />
  );
};

interface PublicationDataFieldRendererProps {
  field: FormField;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onClearFile?: (fieldName: string) => void;
  error?: string;
  disabled?: boolean;
}

const PublicationDataFieldRenderer: React.FC<PublicationDataFieldRendererProps> = ({
  field,
  value,
  onChange,
  onClearFile,
  error,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isFieldDisabled =
    field.type === "READONLY" ||
    field.isEditable === false ||
    field.name === "productionPlantState" ||
    field.name === "productionPlantDistrict" ||
    disabled;

  if (field.type === "UNIT") {
    return (
      <PublicationUnitField
        field={field}
        value={value}
        onChange={onChange}
        error={error}
        disabled={disabled}
      />
    );
  }

  if (field.type === "DROPDOWN") {
    const opts: DropdownOption[] =
      field.dropdown?.options || field.dropdownValues || [];
    const mapped = opts.map((opt) => ({
      label: opt.label,
      value: String(opt.value ?? ""),
    }));
    const selected = (() => {
      if (field.name === "lcaDatabasesUsed") return normalizeLcaDatabaseStoredValue(value);
      if (field.name === "epdProgramme") return normalizeEpdProgrammeStoredValue(value);
      if (field.name === "systemBoundaries") {
        return normalizeSystemBoundaryStoredValue(value);
      }
      return String(value ?? "").trim();
    })();
    const idInList = mapped.some((o) => o.value === selected);
    const savedLabel = (() => {
      if (field.name === "lcaDatabasesUsed") return resolveLcaDatabaseDisplayLabel(selected);
      if (field.name === "epdProgramme") return resolveEpdProgrammeDisplayLabel(selected);
      if (field.name === "systemBoundaries") {
        return resolveSystemBoundaryDisplayLabel(selected);
      }
      return selected;
    })();
    const merged =
      selected && !idInList
        ? [{ value: selected, label: savedLabel || selected }, ...mapped]
        : mapped;

    return (
      <Dropdown
        key={field.id}
        label={field.label}
        name={field.name}
        options={merged}
        value={selected}
        onChange={onChange}
        required={field.isMandatory}
        disabled={isFieldDisabled}
        infoText={field.infoText}
        error={error}
        placeholder="Select an option"
      />
    );
  }

  if (field.type === "FILE") {
    const isPublicationUpload = field.name === "uploadPublicationFile";

    return (
      <div className="form-group publication-file-field">
        <FormFieldLabel
          label={field.label}
          required={field.isMandatory}
          infoText={field.infoText}
        />

        <div className="publication-file-row">
          <input
            ref={fileInputRef}
            id={field.name}
            name={field.name}
            type="file"
            accept={
              isPublicationUpload
                ? "application/pdf,.pdf,image/jpeg,image/png,image/gif,image/webp,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                : undefined
            }
            onChange={onChange}
            required={field.isMandatory}
            disabled={isFieldDisabled}
            className={`text-input publication-file-input ${isFieldDisabled ? "disabled" : ""} ${error ? "error" : ""}`}
          />

          {Boolean(value) && (
            <button
              type="button"
              className="publication-file-clear-btn"
              onClick={() => {
                if (isFieldDisabled) return;
                if (fileInputRef.current) fileInputRef.current.value = "";
                onClearFile?.(field.name);
              }}
              disabled={isFieldDisabled}
            >
              Clear
            </button>
          )}
        </div>

        <div className="publication-file-meta">
          <span className="publication-file-selected" aria-live="polite">
            {value ? value : "No file selected"}
          </span>
          {isPublicationUpload ? (
            <span className="publication-file-hint">
              PDF, Word, or image · max {PUBLICATION_FILE_MAX_SIZE_LABEL}
            </span>
          ) : null}
        </div>

        {error && <span className="error-message">{error}</span>}
      </div>
    );
  }

  const h = getContributorTextInputHtmlProps(field);
  const textValue =
    field.name === "referenceServiceLife"
      ? normalizeReferenceServiceLifeYears(value)
      : value;
  return (
    <TextInput
      key={field.id}
      label={field.label}
      name={field.name}
      value={textValue}
      onChange={onChange}
      required={field.isMandatory}
      disabled={isFieldDisabled}
      type={
        (h.type as string) ??
        (field.type === "EMAIL" ? "email" : "text")
      }
      maxLength={h.maxLength}
      min={h.min}
      max={h.max}
      step={h.step}
      placeholder={field.placeholder || ""}
      infoText={field.infoText}
      error={error}
    />
  );
};

/** React component — Publication Data Field Renderer. */
export default PublicationDataFieldRenderer;

